import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Divider,
  Modal,
} from "@bca/ui-library";

import CountdownTimer from "../CountdownTimer";
import TermsAndConditions from "./TermsAndConditions";
import Spinner from "../Spinner";
import { formatMoney } from "./formatMoney";
import { getTimeUntilSaleEnds } from "../../helpers/getTimeUntilSaleEnds";

interface BuyNowModalProps {
  saleEndDate: string;
  buyNowAmount: number;
  isOpen: boolean;
  termsAndConditionsNeedAccepting: boolean;
  termsAndConditionsLink: string;
  onClose: () => void;
  onBuyNow: () => Promise<void>;
  onAcceptTC: () => Promise<void>;
}

const BuyNowModal: React.FunctionComponent<BuyNowModalProps> = ({
  saleEndDate,
  buyNowAmount,
  isOpen,
  termsAndConditionsNeedAccepting,
  termsAndConditionsLink,
  onClose,
  onBuyNow,
  onAcceptTC,
}) => {
  const showCountdownTimer = getTimeUntilSaleEnds(saleEndDate) <= 60;
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = () => {
    setIsLoading(true);
    if (termsAndConditionsNeedAccepting) {
      return onAcceptTC().then(() => {
        onBuyNow().then(() => {
          setIsLoading(false);
          onClose();
        });
      });
    }
    onBuyNow().then(() => {
      setIsLoading(false);
      onClose();
    });
  };

  return (
    <Modal role="dialog" isOpen={isOpen} onClose={onClose}>
      <Box display="flex" flexDirection="column">
        <Typography variant="h6" m={0}>
          Buy Now
        </Typography>
        <Typography
          css={`
            font-size: 30px;
            font-weight: 500;
          `}
          m={0}
          mb={1}
        >
          {formatMoney(buyNowAmount)}
        </Typography>
      </Box>
      <Box display="flex" mb={3}>
        <Typography variant="body" color="grey-blue" m={0}>
          Excl. Fees
        </Typography>
        {showCountdownTimer && (
          <>
            <Divider
              orientation="vertical"
              width="1px"
              marginLeft={3}
              marginRight={3}
              marginTop={18}
              marginBottom={18}
            />
            <CountdownTimer saleEndDate={saleEndDate} />
          </>
        )}
      </Box>
      <Alert
        paddingX={30}
        paddingY={24}
        width={["auto", 500]}
        title="By pressing Confirm, you commit to buy this vehicle."
      ></Alert>
      {termsAndConditionsNeedAccepting && (
        <TermsAndConditions
          checked={checked}
          setChecked={setChecked}
          termsAndConditionsLink={termsAndConditionsLink}
        />
      )}
      <Box display="flex" justifyContent="space-between" mt={30}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary-gradient"
          onClick={handleConfirm}
          disabled={isLoading || (termsAndConditionsNeedAccepting && !checked)}
          data-en="click"
          data-ec="button"
          data-el="buynow"
          data-ea="confirmation"
          data-ev={buyNowAmount}
          data-es=""
        >
          {isLoading ? <Spinner /> : "Confirm"}
        </Button>
      </Box>
    </Modal>
  );
};

export default BuyNowModal;
